import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname, useSearchParams } from 'src/routes/hooks';

import { getAccessTokenLS } from 'src/utils/localStorageUtils';

import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';

import type { AuthState } from '../../types';

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState, setField } = useSetState<AuthState>({ user: null, loading: true });
  const [isTeantListView, setIsTeantListView] = useState<boolean>(false);

  const checkUserSession = useCallback(async () => {
    try {
      // const accessToken = sessionStorage.getItem(JWT_STORAGE_KEY);
      const accessToken = getAccessTokenLS();

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        setState({ user: { accessToken }, loading: false });
      } else {
        setState({ user: null, loading: false });
        setIsTeantListView(false);
      }
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
      setIsTeantListView(false);
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  // ========================================================================================================================

  const handleTeantView = useCallback((value: boolean) => {
    setIsTeantListView(value);
  }, []);
  // ========================================================================================================================

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const activeStep = pathname.includes('') ? Number(searchParams.get('step')) : null;
  const SIGNUP_STEPS = [
    'User Details',
    'Organization Details',
    'Select Plan',

    // t('FINISH'),
  ];
  const completed = activeStep === SIGNUP_STEPS.length;
  const onChangeStep = useCallback(
    (type: 'back' | 'next', step?: any) => {
      const stepNumbers = {
        back: (activeStep ?? 0) - 1,
        next: (activeStep ?? 0) + 1,
      };

      const targetStep = stepNumbers[type];
      const queryString = new URLSearchParams({ step: `${targetStep}` }).toString();

      // ✅ Match ANY route that includes `/tenant` — works for /tenant, /tenant/123, /tenant/signup, etc.
      const isTenantRoute = isTeantListView || step === true;
      const updatePath = isTenantRoute ? paths.tenant.root : paths.auth.signUp;

      const redirectPath = targetStep === 0 ? updatePath : `${updatePath}?${queryString}`;

      router.push(redirectPath);
    },
    [activeStep, router, isTeantListView]
  );

  // ========================================================================================================================

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (pathname.includes('auth/sign-up')) {
        sessionStorage.setItem('wasOnSignUp', 'true');
      } else {
        sessionStorage.removeItem('wasOnSignUp');
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [pathname]);

  useEffect(() => {
    const wasOnSignUp = sessionStorage.getItem('wasOnSignUp');
    if (wasOnSignUp === 'true') {
      sessionStorage.removeItem('wasOnSignUp');
      router.replace(paths.auth.signUp);
    }
  }, []);

  // ========================================================================================================================
  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user, role: state.user?.role ?? 'admin' } : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      completed,
      onChangeStep,
      steps: SIGNUP_STEPS,
      activeStep: activeStep ?? 0,
      handleTeantView,
    }),
    [checkUserSession, state.user, status, activeStep]
  );
  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
